import { useState, useEffect } from "react";
import { Button, Card, Divider, Radio, Tabs, Input } from "antd";
import { CheckCircleTwoTone, CloseCircleTwoTone } from "@ant-design/icons";
import Title from "antd/es/typography/Title";

const { TextArea } = Input;

const shuffleArray = (array) => {
    if (!Array.isArray(array)) return [];
    return [...array].sort(() => Math.random() - 0.5);
};

const Test = ({ questionsData = [], setQuizNumber, setQuizLength, quizName }) => {
    const [questions, setQuestions] = useState([]);
    const [current, setCurrent] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [correctCount, setCorrectCount] = useState(0);
    const [wrongCount, setWrongCount] = useState(0);
    const [feedback, setFeedback] = useState("");
    const [finished, setFinished] = useState(false);
    const [textAnswers, setTextAnswers] = useState({});

    useEffect(() => {
        if (questionsData && questionsData.length > 0) {
            const shuffledQuestions = shuffleArray(questionsData).map((q) => ({
                ...q,
                options: q.options ? shuffleArray(q.options) : null,
            }));
            setQuestions(shuffledQuestions);
            setQuizLength(questionsData.length);

            const initialTextAnswers = {};
            questionsData.forEach((q, index) => {
                if (q.type === "text_answer") {
                    initialTextAnswers[index] = "";
                }
            });
            setTextAnswers(initialTextAnswers);
        }
    }, [questionsData]);

    useEffect(() => {
        if (questions[current]?.type !== "text_answer" && selectedAnswer !== null) {
            if (selectedAnswer === questions[current]?.answer) {
                setCorrectCount(correctCount + 1);
            } else {
                setWrongCount(wrongCount + 1);
            }
        }
    }, [selectedAnswer]);

    useEffect(() => {
        handleRestart();
    }, [quizName]);

    const handleNext = () => {
        // For text answer questions, calculate score when moving to next question
        if (questions[current]?.type === "text_answer") {
            const answerText = textAnswers[current] || "";
            const charCount = answerText.length;

            if (charCount === 0) {
                // 0 points for no answer
                setWrongCount(wrongCount + 1);
            } else if (charCount >= 50) {
                // 1 point for 100+ characters
                setCorrectCount(correctCount + 1);
            } else {
                // 0.5 points for <100 characters
                setCorrectCount(correctCount + 0.5);
            }
        }

        if (current + 1 < questions.length) {
            setCurrent(current + 1);
            setFeedback("");
            setSelectedAnswer(null); // Reset selected answer for next question
        } else {
            setFinished(true);
            setQuizNumber(correctCount);
        }
    };

    const handlePrev = () => {
        if (current > 0) {
            setCurrent(current - 1);
        }
    };

       const handleRestart = () => {
        const shuffledQuestions = shuffleArray(questionsData || []).map((q) => ({
            ...q,
            options: q.options ? shuffleArray(q.options) : null,
        }));
        setQuestions(shuffledQuestions);
        setCurrent(0);
        setSelectedAnswer(null);
        setCorrectCount(0);
        setWrongCount(0);
        setFinished(false);
        
        const resetTextAnswers = {};
        (questionsData || []).forEach((q, index) => {
            if (q.type === "text_answer") {
                resetTextAnswers[index] = "";
            }
        });
        setTextAnswers(resetTextAnswers);
    };

    const handleTextAnswerChange = (e) => {
        setTextAnswers({
            ...textAnswers,
            [current]: e.target.value
        });
    };

    const tabItems = questions.map((q, index) => ({
        key: String(index),
        label: `${index + 1}`,
        children: (
            <div className="row align-items-center py-3">
                <div className="col-lg-12">
                    <h4 className="card-title">{index + 1}. {q?.quiz}</h4>
                </div>

                {q?.type === "true_false" ? (
                    <div className="flex justify-center gap-4 mb-4">
                        <Button onClick={() => setSelectedAnswer("true")} disabled={feedback !== ""}>
                            True
                        </Button>
                        <Button onClick={() => setSelectedAnswer("false")} disabled={feedback !== ""}>
                            False
                        </Button>
                    </div>
                ) : q?.type === "fill_blank" ? (
                    <div className="mb-4">
                        <h3 className="text-[20px] font-semibold">{index + 1}. {q.quiz.replace("_____", selectedAnswer || "_____")}</h3>
                        <div className="flex justify-center gap-2 mt-4">
                            {q.options.map((option) => (
                                <Button key={option} onClick={() => setSelectedAnswer(option)} disabled={feedback !== ""}>
                                    {option}
                                </Button>
                            ))}
                        </div>
                    </div>
                ) : q?.type === "text_answer" ? (
                    <div className="mb-4">
                        <TextArea
                            rows={6}
                            value={textAnswers[index] || ""}
                            onChange={handleTextAnswerChange}
                            placeholder="Javobingizni yozing..."
                            maxLength={500}
                            showCount
                        />
                        <div className="mt-2 text-sm text-gray-600">
                            {textAnswers[index]?.length >= 50 ? (
                                <span className="text-green-500">50+ belgi - 1 ball</span>
                            ) : textAnswers[index]?.length > 0 ? (
                                <span className="text-yellow-500">{textAnswers[index]?.length} belgi - 0.5 ball</span>
                            ) : (
                                <span className="text-red-500">Javob kiritilmagan - 0 ball</span>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="col-lg-12 text-start">
                        <Radio.Group size="large" onChange={(e) => setSelectedAnswer(e.target.value)} value={selectedAnswer}>
                            {q?.options.map((option) => (
                                <Radio className="para" key={option} value={option} style={{ display: "block", marginBottom: 8 }}>
                                    {option}
                                </Radio>
                            ))}
                        </Radio.Group>
                    </div>
                )}

                {feedback && <p className="text-lg font-semibold">{feedback}</p>}
                <div className="flex justify-content-between mt-[20px] all-btn">
                    <button onClick={handlePrev} className={`btn-p v1 btn-blue rounded btn ed-btn flex gap-[10px] ${current === 0 ? 'd-none' : ''}`} disabled={current === 0}>
                        <span className="btn_icon ms-0 me-2">
                            <i className="fa-solid fa-arrow-left"></i>
                        </span>
                        <span className="btn_text" data-text="Orqaga">
                            Orqaga
                        </span>
                    </button>
                    <button onClick={handleNext} className="btn ed-btn flex gap-[10px] btn-p v1 btn-blue rounded">
                        <span className="btn_text" data-text={current + 1 < questions.length ? "Keyingi" : "Natijani ko‘rish"}>
                            {current + 1 < questions.length ? "Keyingi" : "Natijani ko‘rish"}
                        </span>
                        <span className="btn_icon ms-2">
                            <i className="fa-solid fa-arrow-right"></i>
                        </span>
                    </button>
                </div>
            </div>
        )
    }));

    return (
        <Card className="relative w-96 mx-auto bg-transparent mt-10 card-container1">
            <div className="card-blur-bg" />
            <div className="card-content1">
                {finished ? (
                    <div className="text-center">
                        {
                            questions.length / 2 < correctCount
                                ?
                                <>
                                    <CheckCircleTwoTone style={{ fontSize: 96, marginBottom: 30 }} twoToneColor="#52c41a" />
                                    <Title level={3} style={{ margin: 0 }} type="success">To'g'ri javoblar: {correctCount.toFixed(1)}/{questions.length}</Title>
                                </>
                                :
                                <>
                                    <CloseCircleTwoTone style={{ fontSize: 96, marginBottom: 30 }} twoToneColor="#ff4d4f" />
                                    <Title level={3} type="danger">To'g'ri javoblar: {correctCount.toFixed(1)}/{questions.length}</Title>
                                </>
                        }
                        <div style={{ textAlign: 'right' }} className="all-btn">
                            <button onClick={handleRestart} className="btn-p v1 btn-blue rounded btn ed-btn flex gap-[7px]">
                                <span className="btn_icon me-1">
                                    <i className="fas fa-undo-alt"></i>
                                </span>
                                <span className="btn_text" data-text="Qayta boshlash">
                                    Qayta boshlash
                                </span>
                            </button>
                        </div>
                        <Divider style={{ margin: '20px 0' }} />
                    </div>
                ) : (
                    <Tabs
                        activeKey={String(current)}
                        onChange={(key) => setCurrent(Number(key))}
                        centered
                        size="large"
                        // type="card"
                        items={tabItems}
                        className="custom-tabs"
                    />
                )}
            </div>
        </Card>
    );
};

export default Test;